<template>
    <TransitionRoot
        as="template"
        :show="open"
    >
        <Dialog
            as="div"
            class="fixed inset-0 z-60 overflow-y-auto"
            @close="emit('keep')"
        >
            <div class="flex min-h-screen items-center justify-center px-4 text-center">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay class="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <DialogPanel
                        class="rounded-muval-2 shadow-muval-3 relative z-10 inline-block w-full max-w-md transform overflow-hidden bg-white p-6 text-left align-middle transition-all dark:bg-gray-800"
                        data-test="modal-discard-confirm"
                    >
                        <DialogTitle
                            as="span"
                            class="flex items-center gap-2.5 text-base font-medium"
                        >
                            <SvgIcon
                                name="system-error"
                                class="text-error h-6 w-6"
                            />
                            Are you sure?
                        </DialogTitle>
                        <p class="text-md mt-4">Are you sure you want to discard your unsaved changes?</p>
                        <div class="mt-4 flex flex-col justify-end gap-2 md:flex-row">
                            <!-- First in DOM order, so the focus trap lands here. See the note above. -->
                            <MuvalButton
                                cancelBtn
                                class="order-2 md:order-1"
                                data-test="modal-discard-keep"
                                @click="emit('keep')"
                            >
                                No, keep editing
                            </MuvalButton>
                            <MuvalButton
                                class="order-1 md:order-2"
                                data-test="modal-discard-confirm-button"
                                @click="emit('discard')"
                            >
                                Yes, discard changes
                            </MuvalButton>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
/**
 * The unsaved-changes warning behind a retaining modal's X (CU-86d3wvwq7).
 *
 * A HeadlessUI Dialog rendered inside the parent modal's panel: a nested HeadlessUI dialog is the
 * one kind that stacks correctly there — the parent stops treating clicks on it as outside-clicks,
 * stops handling Escape, and is made inert while this is open. Neither `MuvalModal` (it would import
 * itself) nor `MPModal` (a reka dialog portalled to body is inerted by the parent) can do that job.
 *
 * `z-60`, one above the `z-50` both base modals sit at. HeadlessUI portals every Dialog to
 * `#headlessui-portal-root` and reka teleports to `body`; neither opens a stacking context, so a
 * z-50 tie is broken by tree order. A retained-but-hidden MuvalModal keeps the portal root alive,
 * and an MPModal opened after it lands *after* that root — which would leave this confirm painting
 * behind the modal it has just inerted, reading as a hang.
 *
 * No `:initial-focus`: MuvalButton's root is an unfocusable wrapper `div`, and passing it both
 * fails to focus and suppresses HeadlessUI's first-focusable fallback. Left null, the trap focuses
 * "No, keep editing" — first in DOM order, `order-2 md:order-1` reorders it visually only.
 *
 * Keep the template's root a single `Dialog` with no sibling comment nodes: `TransitionRoot
 * as="template"` forwards a ref to its one child and throws "Passing props on template!" otherwise.
 */
import { Dialog, DialogOverlay, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import MuvalButton from '@buttons/MuvalButton.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';

defineProps<{
    open: boolean;
}>();

const emit = defineEmits<{
    keep: [];
    discard: [];
}>();
</script>
